import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Text,
  VStack,
  HStack,
  Box,
  useToast,
} from '@chakra-ui/react';
import { IoBusinessOutline } from 'react-icons/io5';
import { useState } from 'react';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';

interface CreateTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  accessToken: string;
  onSuccess?: () => void;
}

const CreateTeamModal = ({ isOpen, onClose, accessToken, onSuccess }: CreateTeamModalProps) => {
  const router = useRouter();
  const toast = useToast();
  // update() was added in next-auth v4.22 — guard before calling
  const { update } = useSession() as any;
  const api_url = process.env.NEXT_PUBLIC_API_URL;

  const [teamName, setTeamName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    setTeamName('');
    setError('');
    onClose();
  };

  const handleCreate = async () => {
    const trimmed = teamName.trim();
    if (!trimmed) {
      setError('Bitte gib einen Team-Namen ein.');
      return;
    }
    if (trimmed.length < 2) {
      setError('Der Team-Name muss mindestens 2 Zeichen lang sein.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const res = await fetch(`${api_url}/users/me/teams`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ name: trimmed }),
      });

      if (!res.ok) {
        let msg = 'Team konnte nicht erstellt werden.';
        try {
          const j = await res.json();
          if (j?.message) msg = Array.isArray(j.message) ? j.message.join(', ') : String(j.message);
        } catch (_) {}
        setError(msg);
        return;
      }

      // The backend already switched the active team to the newly created one.
      // Refresh the NextAuth session so session.team_data reflects the new team
      // BEFORE navigating to /setup — otherwise the wizard renders with the
      // previously active team's data.
      handleClose();
      onSuccess?.();
      if (typeof update === 'function') {
        await update();
        await router.push('/setup');
      } else {
        // Fallback for older next-auth: hard navigation re-runs the session callback.
        await router.push('/setup');
        router.reload();
      }
    } catch (err) {
      setError('Team konnte nicht erstellt werden. Bitte versuche es erneut.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="md">
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent borderRadius="xl" boxShadow="2xl">
        <ModalHeader pb={2}>
          <HStack spacing={3}>
            <Box
              w={9}
              h={9}
              borderRadius="lg"
              bg="rekening.500"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <IoBusinessOutline color="white" size={18} />
            </Box>
            <VStack spacing={0} align="start">
              <Text fontSize="md" fontWeight="bold">Neues Team erstellen</Text>
              <Text fontSize="xs" color="gray.500" fontWeight="normal">
                Du wirst automatisch Eigentümer und Admin
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pt={2} pb={4}>
          <FormControl isInvalid={!!error}>
            <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">
              Team-Name
            </FormLabel>
            <Input
              placeholder="z.B. Acme GmbH"
              value={teamName}
              onChange={(e) => {
                setTeamName(e.target.value);
                if (error) setError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
              }}
              focusBorderColor="rekening.500"
              borderRadius="lg"
              size="md"
              autoFocus
            />
            {error && <FormErrorMessage>{error}</FormErrorMessage>}
          </FormControl>
        </ModalBody>
        <ModalFooter pt={0} gap={2}>
          <Button
            variant="ghost"
            onClick={handleClose}
            size="sm"
            isDisabled={isLoading}
          >
            Abbrechen
          </Button>
          <Button
            colorScheme="rekening"
            onClick={handleCreate}
            size="sm"
            isLoading={isLoading}
            loadingText="Wird erstellt…"
            isDisabled={!teamName.trim()}
          >
            Team erstellen
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateTeamModal;
